import { createContext, useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import JSZip from "jszip"
import fetchProgress from 'fetch-progress'
import throttle from 'throttleit';

import { socketActions } from '@/redux/actions'
import { socketClient, getCookie } from '@/utils'
import { LANGUAGE_LOCALES_CODE, COOKIE_AUTH_TOKEN } from '@/constants'
import { getDateTimeNow } from '@/utils/formatDate'
import Toggles from '@domain/toggles/Toggles'

export const WebSocketContext = createContext()

let socket = null

let multipleDocuments = []
let downloadsReady = false
let abortControllers = {}

export const WebSocketProvider = (props) => {
	const { children } = props
	const { locale } = useIntl()
	const dispatch = useDispatch()

	const userAccount = useSelector(state => state.auth.account, shallowEqual)
	const authToken = useSelector(state => state.auth.token)

	useEffect(() => {
		socket = socketClient(true)
		
		return () => {
			socket.disconnect()
		}
	}, [])

	const socketEmit = (event, args, catalogId) => {
		const refreshToken = getCookie('vibia_refresh')

		const params = {
			docsParams: args,
			lang: LANGUAGE_LOCALES_CODE[locale],
			catalogId,
			user: {
				crm: userAccount?.crm,
				rolId: userAccount?.rolId,
				email: userAccount?.email,
				id: userAccount?.id
			},
			token: authToken || getCookie(COOKIE_AUTH_TOKEN),
			refreshToken: refreshToken
		}

		if (socket.disconnected) {
			socket.connect()
		}

		dispatch(
			socketActions.startDownload({
				downloading: true,
				response: '',
				progress: {},
				showInfo: true
			})
		)

		socket.emit(event, params)
	}

	const socketDisconnect = () => {
		socket.disconnect()
	}

	useEffect(() => {
		socket.on('progressV2', handleProgress)
		socket.on('download-binary', handleDownloadBinary)
		socket.on('download-url', handleDownloadUrl)
		socket.on('download-hd', handleDownloadUrl)
		socket.on('download-ready', handleDownloadsReady)

		return () => {
			socket.off('progressV2')
			socket.off('download-binary')
			socket.off('download-url')
			socket.off('download-hd')
			socket.off('download-ready')
		}
	}, [socket])

	const handleDownloadUrl = async (resp) => {
		const file = ({ type: "url", ...resp })
		
		let response
		let abortController = new AbortController()
		const { signal } = abortController
		abortControllers[file.filename] = abortController
		const throttledDispatchProgress = throttle(
			(progressUpdate) => dispatch(socketActions.downloadProgress(progressUpdate)
		), 1000);
		response = await (fetch(file.url, {signal}).then(
			fetchProgress({
			  onProgress(progress) {
				const downloadProgress = 8 * (progress.percentage/ 100)
				const progressUpdate = {
					[file.docname]:{
						currentProgress: 8 + downloadProgress,
						totalProgress: 17
					}
				}
				if(!signal.aborted) throttledDispatchProgress(progressUpdate)
			  },
			})
		));
					
		if(response.status === 200) {
			const blob = await response.blob();
			delete abortControllers[file.filename]
			downloadFinished({ type: "binary", filename: file.filename, data: blob, docname: file.docname });
		} else {
			delete abortControllers[file.filename]
			const progressUpdate = {
				[file.docname]:{
					currentProgress: 3,
					totalProgress: 17
				}
			}
			dispatch(socketActions.downloadProgress(progressUpdate))
		}
	}
	
	const handleDownloadBinary = ({file: buffer, filename, docname}) => {
		try{
			let file
			if(buffer) {
				const data = new Blob([buffer])
				file = { type: "binary", filename , data, docname }
				
				const progressUpdate = {
					[docname]:{
						currentProgress: 16,
						totalProgress: 17
					}
				}
				dispatch(socketActions.downloadProgress(progressUpdate))

			}else{
				throw new Error('Missing file buffer')
			}
			
			if(file) downloadFinished(file)
		}catch{
			const progressUpdate = {
				[docname]:{
					currentProgress: 3,
					totalProgress: 17
				}
			}
			if(docname) dispatch(socketActions.downloadProgress(progressUpdate))
		}
	}
	
	const handleDownloadsReady = async () => {
		downloadsReady = true
		checkIfAllDownloadsFinished()
	}

	const downloadFinished = (file) => {
		multipleDocuments.push(file)

		if(!Toggles.isEnabled("clientSideCompressMultipleDocuments")){
			const progressUpdate = {
				[file.docname]:{
					currentProgress: 17,
					totalProgress: 17
				}
			}
			dispatch(socketActions.downloadProgress(progressUpdate))
			downloadFile(file)
		}

		checkIfAllDownloadsFinished()
	}

	const checkIfAllDownloadsFinished = async () => {
		if(Object.keys(abortControllers).length === 0 && downloadsReady){
			if (Toggles.isEnabled("clientSideCompressMultipleDocuments")) {
				await createUnifiedZip(multipleDocuments)
			}
			resetSocket()
			dispatch(socketActions.downloadComplete()) // Makes all progressbars become checkboxes
		}
	}

	const downloadFile = async (file) => {
		let { url, type, filename, data } = file;

		if (type === "binary") {
			url = URL.createObjectURL(data)
		}
		const a = document.createElement('a')
		a.href = url
		a.download = filename
		a.target = "_blank"
		document.body.appendChild(a);
		a.click()
		document.body.removeChild(a);
		if (type === "binary") {
			URL.revokeObjectURL(url);
		}
	}

	async function createUnifiedZip(files) {
		const zip = new JSZip();

		let emptyZip = true
		console.log({files})
		for (const file of files) {
			if (file.type === 'binary') { // Both flows
				// Defensive: replace any '/' in the filename so JSZip never creates subfolders.
				const flatFilename = (file.filename || '').replace(/\//g, '_')
				zip.file(flatFilename, file.data);
				emptyZip = false
			}
		}

		if(emptyZip) return

		const unifiedBlob = await zip.generateAsync({ type: 'blob' });

		const file = {
			type: 'binary',
			filename: `vibia-download-${getDateTimeNow()}.zip`,
			data: unifiedBlob
		}
		await downloadFile(file)
	}

	const resetSocket = () => {
		downloadsReady = false
		abortControllers = {}
		multipleDocuments = []
	}

	const cancelDownload = async () => {
		for(const key of Object.keys(abortControllers)){
			abortControllers[key].abort()
		}
		resetSocket()
		multipleDocuments = []
		dispatch(socketActions.undoDownload())
	}

	const handleProgress = (resp) => {
		dispatch(socketActions.downloadProgress(resp))
	}

	return (
		<WebSocketContext.Provider
			value={{
				socketEmit,
				socketDisconnect,
				cancelDownload
			}}
		>
			{children}
		</WebSocketContext.Provider>
	)
}

export const useSocketContext = () => useContext(WebSocketContext)
